import { Check, CheckCheck, ClipboardList, Pencil, PlayCircle, Trash2, UsersRound } from 'lucide-react'
import SharedMemberPicker from '../ui/SharedMemberPicker'

export default function CardActionsPanel({
    t,
    card,
    canWorkOnCard,
    doneForCurrentUser,
    effectiveTimerStartedAt,
    cardActionPending,
    onStart,
    onComplete,
    completionFields,
    normalizedCompletionPayload,
    members,
    selectedKpiUserId,
    onSelectKpiUser,
    displayCompletionKpis,
    canCreate,
    onToggleCardShared,
    sharedCardPicker,
    onOpenSharedCardPicker,
    companyMembers,
    onConfirmCardShared,
    onUpdateCardSharedMembers,
    onCancelSharedCardPicker,
    canDeleteCard,
    onDeleteRequest,
}) {
    const byUser = normalizedCompletionPayload.by_user || {}

    return (
        <div className="card-detail-sidebar-section">
            {canWorkOnCard && !doneForCurrentUser && !effectiveTimerStartedAt && !cardActionPending && (
                <button
                    className="card-detail-action start-action"
                    onClick={onStart}
                >
                    <PlayCircle size={16} />
                    {t('dashboard.actions.start')}
                </button>
            )}

            {canWorkOnCard && !doneForCurrentUser && !cardActionPending && (
                <button
                    className="card-detail-action done-action"
                    onClick={onComplete}
                >
                    <CheckCheck size={16} />
                    {t('dashboard.actions.complete')}
                </button>
            )}

            {completionFields.length > 0 && (() => {
                const completedCount = members.filter((member) => {
                    const value = byUser[member.id]?.values
                    if (!value) return false
                    return completionFields.every((field) => String(value[field.key] ?? '').trim() !== '')
                }).length
                const totalCount = members.length || 1
                const allDone = completedCount === totalCount && totalCount > 0

                return (
                    <div className="cd-kpi-summary">
                        <div className="cd-kpi-summary-header">
                            <ClipboardList size={16} />
                            <span>{t('card.kpi.heading')}</span>
                            <span
                                className={`cd-kpi-summary-badge ${allDone ? 'all-done' : 'partial'}`}
                                title={t('card.kpi.completedByCount', { defaultValue: `${completedCount} / ${totalCount} angajați au completat` })}
                                style={{
                                    marginLeft: 'auto',
                                    fontSize: 11,
                                    padding: '2px 8px',
                                    borderRadius: 999,
                                    background: allDone ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.15)',
                                    color: allDone ? 'var(--accent-green, #16a34a)' : 'var(--text-secondary)',
                                }}
                            >
                                {allDone ? '✓ ' : ''}{completedCount}/{totalCount}
                            </span>
                        </div>
                        {members.length > 1 && (
                            <select
                                className="inline-input cd-field-select cd-kpi-user-select"
                                value={selectedKpiUserId || card?.completion_submitted_by || members[0]?.id || ''}
                                onChange={(event) => onSelectKpiUser(event.target.value)}
                            >
                                {members.map((member) => {
                                    const value = byUser[member.id]?.values
                                    const done = value && completionFields.every((field) => String(value[field.key] ?? '').trim() !== '')
                                    return (
                                        <option key={member.id} value={member.id}>
                                            {done ? '✓ ' : '○ '}{member.full_name}
                                        </option>
                                    )
                                })}
                            </select>
                        )}
                        <div className="cd-kpi-summary-list">
                            {completionFields.map((field) => {
                                const currentValue = displayCompletionKpis?.[field.key]
                                const filled = String(currentValue ?? '').trim() !== ''
                                return (
                                    <div key={field.key} className="cd-kpi-summary-item">
                                        <span className="cd-kpi-summary-label">{field.label}</span>
                                        <span
                                            className={`cd-kpi-summary-value ${filled ? 'filled' : ''}`.trim()}
                                            style={filled ? {
                                                color: 'var(--accent-green, #16a34a)',
                                                fontWeight: 600,
                                            } : undefined}
                                        >
                                            {filled ? `✓ ${currentValue}` : t('card.kpi.notFilled')}
                                        </span>
                                    </div>
                                )
                            })}
                        </div>
                        <p className="cd-kpi-summary-note">
                            {t('card.kpi.note')}
                        </p>
                    </div>
                )
            })()}

            {canCreate && (
                <div className="shared-card-wrapper">
                    <button
                        className={`card-detail-action ${card.is_shared ? 'shared-active' : ''}`}
                        onClick={onToggleCardShared}
                    >
                        <UsersRound size={16} />
                        {card.is_shared ? t('card.shared.toggleOn') : t('card.shared.toggleOff')}
                    </button>
                    {card.is_shared && canCreate && (
                        <button
                            className="card-detail-action shared-edit-members"
                            onClick={onOpenSharedCardPicker}
                        >
                            <Pencil size={14} />
                            {t('card.shared.editMembers')}
                        </button>
                    )}
                    {sharedCardPicker && (
                        <SharedMemberPicker
                            allMembers={members.length > 0 ? members : (companyMembers || [])}
                            selected={card.is_shared ? (card.shared_member_ids?.length > 0 ? card.shared_member_ids : members.map((member) => member.id)) : []}
                            onConfirm={card.is_shared ? onUpdateCardSharedMembers : onConfirmCardShared}
                            onCancel={onCancelSharedCardPicker}
                        />
                    )}
                </div>
            )}

            {card.is_shared && (() => {
                const sharedIds = (card.shared_member_ids || []).length > 0 ? card.shared_member_ids : members.map((member) => member.id)
                const sharedMembers = (companyMembers || []).filter((member) => sharedIds.includes(member.id))
                return sharedMembers.length > 0 ? (
                    <div className="card-shared-completions">
                        {sharedMembers.map((member) => {
                            const completed = (card.card_completions || []).some((completion) => completion.user_id === member.id)
                            return (
                                <div key={member.id} className={`card-shared-member ${completed ? 'done' : ''}`}>
                                    <span className="card-shared-member-avatar">
                                        {member.full_name?.charAt(0).toUpperCase() || '?'}
                                    </span>
                                    <span className="card-shared-member-name">{member.full_name}</span>
                                    {completed && <Check size={12} />}
                                </div>
                            )
                        })}
                    </div>
                ) : null
            })()}

            {canDeleteCard && (
                <button className="card-detail-action danger" onClick={onDeleteRequest}>
                    <Trash2 size={16} />
                    {t('card.deleteCard.confirm')}
                </button>
            )}
        </div>
    )
}
